/**
 * Page de tarification (Pricing) avec les plans SaaS dynamiques
 * Architecture: Component → Hook (TanStack Query) → Service (subscriptionsAPI)
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import {
  useSubscriptionPlans,
  useCurrentSubscription,
  useCreateCheckoutSession,
  useOpenBillingPortal,
} from '@/hooks/useSubscriptions';

// Mapping des features par plan pour l'affichage
const PLAN_FEATURES: Record<string, string[]> = {
  STARTER: [
    'Devis & Factures illimités',
    'Clients illimités',
    'Trésorerie automatique',
    'Factur-X & Chorus Pro',
    'Paiements & Relances',
    'Numérotation légale',
    'Support email',
  ],
  PRO: [
    'Tout Starter +',
    'Multi-utilisateurs (3 inclus)',
    'Export comptable (Sage, Cegid)',
    'Rapprochement bancaire',
    'Gestion des dépenses & OCR',
    'Pré-comptabilité automatique',
    'Signature électronique',
    'Archivage probant 10 ans',
    'Support prioritaire',
  ],
  ENTERPRISE: [
    'Tout Pro +',
    'Multi-utilisateurs (10 inclus)',
    'Multi-sociétés illimitées',
    'API publique complète',
    'Webhooks personnalisés',
    'SLA 99.9% garanti',
    'Onboarding dédié',
    'Formation équipe incluse',
  ],
};

// Descriptions des plans
const PLAN_DESCRIPTIONS: Record<string, string> = {
  STARTER: 'Pour les indépendants',
  PRO: 'Pour les PME & Artisans',
  ENTERPRISE: 'Groupes BTP & Cabinets',
};

export default function PricingPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [selectingPlan, setSelectingPlan] = useState<string | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);

  // TanStack Query hooks — données serveur
  const { data: plans = [], isLoading: plansLoading, error: plansError, refetch: refetchPlans } = useSubscriptionPlans();
  const { data: currentSubscription } = useCurrentSubscription();

  // Mutations
  const checkoutMutation = useCreateCheckoutSession();
  const portalMutation = useOpenBillingPortal();

  // Extraire le plan actuel depuis la subscription
  const currentPlanCode = (() => {
    if (!currentSubscription) return null;
    const sub = currentSubscription as Record<string, any>;
    return sub.SubscriptionPlan?.code || sub.plan?.code || null;
  })();

  const isTrialExpired = searchParams.get('expired') === 'true';

  const handleSelectPlan = async (planCode: string) => {
    setSelectingPlan(planCode);
    setPortalError(null);
    try {
      // If user already has an active subscription, redirect to Billing Portal for plan change
      if (currentPlanCode && currentPlanCode !== 'FREE' && currentPlanCode !== 'TRIAL') {
        await portalMutation.mutateAsync();
        return;
      }

      await checkoutMutation.mutateAsync({ planCode, billingCycle });
    } catch (err: any) {
      setPortalError(err instanceof Error ? err.message : 'Erreur lors de la redirection vers le paiement');
      setSelectingPlan(null);
    }
  };

  const getAnnualPrice = (monthlyPrice: number) => {
    // 22% de réduction pour l'annuel (comme sur la landing page)
    return Math.round(monthlyPrice * 0.78);
  };

  const getDisplayPrice = (plan: { code: string; priceMonthly: number }) => {
    if (plan.code === 'FREE') return 0;
    return billingCycle === 'monthly' ? plan.priceMonthly : getAnnualPrice(plan.priceMonthly);
  };

  const isPlanHighlighted = (planCode: string) => {
    return planCode === 'PRO';
  };

  if (plansLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center bg-white/20 backdrop-blur-xl rounded-3xl p-8 shadow-xl shadow-gray-300/10 border border-white/30">
          <Loader2 className="animate-spin h-12 w-12 text-teal-600 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement des plans...</p>
        </div>
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center bg-white/20 backdrop-blur-xl rounded-3xl p-8 shadow-xl shadow-gray-300/10 border border-white/30">
          <p className="text-red-600 mb-4">{plansError instanceof Error ? plansError.message : 'Erreur de chargement'}</p>
          <button
            onClick={() => refetchPlans()}
            className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Filtrer les plans payants actifs (exclure FREE et TRIAL)
  const paidPlans = plans.filter(p => p.code !== 'FREE' && p.code !== 'TRIAL' && p.isActive);

  return (
    <div className="min-h-screen">
      {/* Banner Trial Expiré */}
      {isTrialExpired && (
        <div className="mx-4 mt-6 bg-gradient-to-r from-red-500/90 to-red-600/90 backdrop-blur-xl text-white px-8 py-8 rounded-3xl shadow-xl shadow-red-500/20 border border-red-400/30">
          <div className="max-w-7xl mx-auto text-center">
            <AlertCircle className="h-10 w-10 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">
              Votre période d'essai gratuit de 14 jours est terminée
            </h2>
            <p className="text-lg text-white/90">
              Choisissez un plan pour continuer à utiliser la plateforme
            </p>
          </div>
        </div>
      )}

      {/* Current Subscription Banner */}
      {currentPlanCode && currentPlanCode !== 'FREE' && currentPlanCode !== 'TRIAL' && (
        <div className="mx-4 mt-6 bg-white/40 backdrop-blur-xl px-8 py-6 rounded-3xl shadow-xl shadow-gray-300/10 border border-white/30">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {t('subscriptions.billingPortal.title', { plan: t(`subscriptions.plans.${currentPlanCode}`, currentPlanCode) })}
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                {t('subscriptions.billingPortal.description')}
              </p>
            </div>
            <button
              onClick={async () => {
                setPortalError(null);
                try {
                  await portalMutation.mutateAsync();
                } catch (err: any) {
                  const msg = err.message || '';
                  if (msg.includes('not configured')) {
                    setPortalError(t('subscriptions.errors.stripeNotConfigured'));
                  } else if (msg.includes('No Stripe customer') || msg.includes('no customer')) {
                    setPortalError(t('subscriptions.errors.noStripeCustomer'));
                  } else if (msg.includes('Billing Portal')) {
                    setPortalError(t('subscriptions.errors.billingPortalFailed'));
                  } else {
                    setPortalError(t('subscriptions.errors.portalGeneric'));
                  }
                }
              }}
              disabled={portalMutation.isPending}
              className="flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-semibold transition-colors whitespace-nowrap disabled:opacity-50"
            >
              {portalMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              {t('subscriptions.billingPortal.button')}
            </button>
          </div>
          {portalError && (
            <div className="max-w-7xl mx-auto mt-3 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{portalError}</p>
            </div>
          )}
        </div>
      )}

      <div className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            Tarifs simples et transparents
          </h1>
          <p className="mt-4 text-xl text-gray-600">
            Choisissez la formule adaptée à votre activité
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center gap-1 p-1.5 rounded-2xl bg-white/20 backdrop-blur-xl border border-white/30 shadow-xl shadow-gray-300/10">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white/70 text-teal-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/30'
              }`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                billingCycle === 'annual'
                  ? 'bg-white/70 text-teal-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/30'
              }`}
            >
              Annuel
              <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 text-xs font-bold">
                -22%
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        {paidPlans.length > 0 ? (
          <div className={`grid gap-8 ${
            paidPlans.length === 1 ? 'max-w-md mx-auto' :
            paidPlans.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' :
            paidPlans.length === 3 ? 'md:grid-cols-3' :
            'lg:grid-cols-4 md:grid-cols-2'
          }`}>
            {paidPlans.map((plan) => {
              const isHighlighted = isPlanHighlighted(plan.code);
              const features = PLAN_FEATURES[plan.code] || [];

              return (
                <div
                  key={plan.id}
                  className={`
                    relative rounded-3xl overflow-hidden transition-all duration-500
                    ${isHighlighted
                      ? 'bg-white/40 backdrop-blur-xl ring-2 ring-teal-500 md:scale-105 shadow-2xl shadow-teal-500/20 border border-teal-200/50'
                      : 'bg-white/20 backdrop-blur-xl shadow-xl shadow-gray-300/10 border border-white/30 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1'
                    }
                  `}
                >
                  {isHighlighted && (
                    <div className="absolute top-0 right-0 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-1.5 text-xs font-bold rounded-bl-2xl">
                      POPULAIRE
                    </div>
                  )}

                  <div className="p-8">
                    {/* Plan Header */}
                    <div className="mb-6">
                      <h3 className={`text-2xl font-bold ${isHighlighted ? 'text-teal-600' : 'text-gray-900'}`}>
                        {plan.name}
                      </h3>
                      <p className="text-gray-600 text-sm mt-1">
                        {PLAN_DESCRIPTIONS[plan.code] || ''}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="mb-8">
                      <div className="flex items-baseline">
                        <span className={`text-5xl font-extrabold ${isHighlighted ? 'text-teal-600' : 'text-gray-900'}`}>
                          {getDisplayPrice(plan)}€
                        </span>
                        <span className="ml-2 text-gray-600">/mois HT</span>
                      </div>
                      {billingCycle === 'annual' && (
                        <p className="mt-2 text-sm text-teal-600 font-medium">
                          Économisez {Math.round(plan.priceMonthly * 12 * 0.22)}€/an
                        </p>
                      )}
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-8">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className={`h-5 w-5 mr-3 flex-shrink-0 mt-0.5 ${
                            isHighlighted ? 'text-teal-500' : 'text-gray-400'
                          }`} />
                          <span className={`text-sm ${
                            feature.startsWith('Tout') ? 'font-semibold text-gray-900' : 'text-gray-700'
                          }`}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <button
                      onClick={() => handleSelectPlan(plan.code)}
                      disabled={selectingPlan === plan.code || currentPlanCode === plan.code}
                      className={`
                        w-full py-3.5 px-4 rounded-2xl font-semibold text-white
                        transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                        ${isHighlighted
                          ? 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-lg shadow-teal-500/30 hover:shadow-xl'
                          : 'bg-gray-800 hover:bg-gray-900 hover:shadow-lg'
                        }
                      `}
                    >
                      {selectingPlan === plan.code ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Redirection...
                        </span>
                      ) : currentPlanCode === plan.code ? (
                        'Plan actuel'
                      ) : currentPlanCode && currentPlanCode !== 'FREE' && currentPlanCode !== 'TRIAL' ? (
                        'Changer de plan'
                      ) : (
                        plan.code === 'ENTERPRISE' ? 'Nous contacter' : 'Commencer'
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            Aucun plan payant disponible pour le moment.
          </div>
        )}

        {/* FAQ / Additional Info */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-white/20 backdrop-blur-xl rounded-3xl px-8 py-6 shadow-xl shadow-gray-300/10 border border-white/30">
            <p className="text-gray-700">
              Tous les plans payants incluent une période d'essai de <strong className="text-teal-600">14 jours gratuits</strong>.
              <br />
              Annulez à tout moment sans frais.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Besoin d'un plan sur-mesure pour votre entreprise ?{' '}
              <a href="mailto:contact@facturxpro.com" className="text-teal-600 hover:underline font-medium">
                Contactez-nous
              </a>
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
